import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import EventCalendar from '../components/event/EventCalendar';
import EventCreateDialog from '../components/event/EventCreateDialog';
import { LoadingState, useErrorToast } from '../components/common';
import { toSafeImageUrl } from '../components/common/safeUrl';
import {
  EVENT_STATUS_META,
  RECORD_ROOM_META,
  getEventStatus,
  isRecordRoom,
  type EventStatus,
} from '../components/event/eventStatus';
import { EVENT_TYPE_META } from '../components/event/eventType';
import { useEventContext } from '../contexts/EventContext';
import { buildEventScopedPath } from '../routes/appRoutes';
import type { ManagerRole } from '../api/managerApi';
import { eventService, type EventSummary } from '../services/eventService';
import { loadMyManagerProfile } from '../services/managerService';

type ViewMode = 'grid' | 'calendar';
type StatusFilter = 'all' | EventStatus;

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'upcoming', label: '진행 예정' },
  { key: 'ongoing', label: '진행 중' },
  { key: 'ended', label: '종료' },
];

// 단색 휴지통 아이콘 (currentColor 상속 → IconButton 색을 그대로 따라감)
function TrashIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

// 단색 복제 아이콘 (currentColor 상속 → IconButton 색을 그대로 따라감)
function CopyIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function formatDateKo(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${value} (${WEEKDAYS[date.getDay()]})`;
}

function EventListPage() {
  const navigate = useNavigate();
  const { setSelectedEventId } = useEventContext();
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState<ViewMode>('grid');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const { showError, errorToast } = useErrorToast();
  const canManageEvents = managerRole === 'SUPER' || managerRole === 'MIDDLE';

  const statusCounts = useMemo(() => {
    const counts = { all: events.length, upcoming: 0, ongoing: 0, ended: 0 };
    for (const event of events) {
      counts[getEventStatus(event)] += 1;
    }
    return counts;
  }, [events]);

  const visibleEvents = useMemo(
    () =>
      statusFilter === 'all'
        ? events
        : events.filter((event) => getEventStatus(event) === statusFilter),
    [events, statusFilter]
  );

  useEffect(() => {
    eventService
      .list()
      .then((data) => {
        setEvents(data);
      })
      .catch((error) => {
        showError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [showError]);

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  const goToEvent = (event: EventSummary) => {
    setSelectedEventId(event.id, event.title);
    navigate(buildEventScopedPath('/events/info', event.id));
  };

  const handleDelete = async (
    e: React.MouseEvent<HTMLButtonElement>,
    event: EventSummary
  ) => {
    e.stopPropagation();
    if (!canManageEvents) return;
    setDeleteTarget(event);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await eventService.delete(deleteTarget.id);
      setEvents((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      showError(error);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreated = (created: EventSummary) => {
    setEvents((prev) => [created, ...prev]);
  };

  const handleDuplicate = async (
    e: React.MouseEvent<HTMLButtonElement>,
    event: EventSummary
  ) => {
    e.stopPropagation();
    if (!canManageEvents || duplicatingId !== null) return;

    setDuplicatingId(event.id);
    try {
      const duplicated = await eventService.duplicate(event.id);
      setEvents((prev) => [duplicated, ...prev]);
    } catch (error) {
      showError(error);
    } finally {
      setDuplicatingId(null);
    }
  };

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <div className="page-title-block">
          <p className="page-section">Event</p>
          <h1>대회 목록</h1>
          <p className="page-description">등록된 대회를 확인하고 새 대회를 추가하거나 상세 관리 화면으로 이동합니다.</p>
        </div>
        {/* 헤더: (제목 + 뷰 토글) + 추가 버튼 */}
        {canManageEvents && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              mb: 3,
            }}
          >
            <Button
              variant="contained"
              disableElevation
              onClick={() => setCreateOpen(true)}
              sx={{
                backgroundColor: '#1da1f2',
                fontWeight: 700,
                '&:hover': { backgroundColor: '#1791db' },
              }}
            >
              + 대회 추가
            </Button>
          </Box>
        )}

        {loading ? (
          <LoadingState message="대회 목록을 불러오는 중입니다." />
        ) : (
          <>
            {/* 툴바: 상태 필터(좌, 카드뷰에서만) + 뷰 토글(우) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                minHeight: 32,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                {view === 'grid' &&
                  events.length > 0 &&
                  STATUS_FILTERS.map((filter) => {
                    const selected = statusFilter === filter.key;
                    return (
                      <Chip
                        key={filter.key}
                        label={`${filter.label} ${statusCounts[filter.key]}`}
                        size="small"
                        onClick={() => setStatusFilter(filter.key)}
                        sx={{
                          height: 28,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                          color: selected ? '#0c63a8' : '#64748b',
                          backgroundColor: selected ? '#e8f6fe' : '#f1f5f9',
                          border: `1px solid ${selected ? '#bfe3fb' : 'transparent'}`,
                          '&:hover': { backgroundColor: selected ? '#e8f6fe' : '#e9eef4' },
                        }}
                      />
                    );
                  })}
              </Box>
              <ToggleButtonGroup
                value={view}
                exclusive
                size="small"
                onChange={(_, next: ViewMode | null) => next && setView(next)}
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#64748b',
                    borderColor: '#e2e8f0',
                    textTransform: 'none',
                  },
                  '& .Mui-selected': {
                    color: '#0c63a8 !important',
                    backgroundColor: '#e8f6fe !important',
                  },
                }}
              >
                <ToggleButton value="grid">카드</ToggleButton>
                <ToggleButton value="calendar">캘린더</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {view === 'calendar' && (
              <EventCalendar events={events} onSelect={goToEvent} />
            )}
            {view !== 'calendar' && events.length === 0 && (
              <Box sx={{ color: '#8a94a6', fontSize: 14, py: 6 }}>등록된 대회가 없습니다.</Box>
            )}
            {view !== 'calendar' && events.length > 0 && visibleEvents.length === 0 && (
              <Box sx={{ color: '#8a94a6', fontSize: 14, py: 6 }}>
                해당 상태의 대회가 없습니다.
              </Box>
            )}
            {view !== 'calendar' && events.length > 0 && visibleEvents.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 2.5,
                }}
              >
                {visibleEvents.map((event) => {
                  const safeImageUrl = toSafeImageUrl(event.imageUrl);

                  return (
              <Paper
                key={event.id}
                variant="outlined"
                onClick={() => goToEvent(event)}
                sx={{
                  borderColor: '#e8edf3',
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.15s',
                  '&:hover': {
                    borderColor: '#bfe3fb',
                    boxShadow: '0 6px 20px rgba(29,161,242,0.12)',
                    transform: 'translateY(-2px)',
                  },
                  // 카드에 hover(또는 액션 버튼 포커스) 시에만 복제·삭제 버튼 노출
                  '&:hover .action-btn, & .action-btn:focus-visible': { opacity: 1 },
                  // 같은 자리(우측 상단)에 액션 버튼이 뜰 때는 기록실 태그를 숨겨 겹치지 않게 한다
                  ...(canManageEvents && {
                    '&:hover .record-room-tag, &:has(.action-btn:focus-visible) .record-room-tag': {
                      opacity: 0,
                    },
                  }),
                }}
              >
                {/* 포스터 + 상태 오버레이 */}
                <Box
                  sx={{
                    position: 'relative',
                    aspectRatio: '3 / 4',
                    backgroundColor: '#f1f5f9',
                    overflow: 'hidden',
                  }}
                >
                  {safeImageUrl ? (
                    <Box
                      component="img"
                      src={safeImageUrl}
                      alt={event.title}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#cbd5e1',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      포스터 없음
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      display: 'flex',
                      gap: 0.5,
                    }}
                  >
                    <Chip
                      label={EVENT_STATUS_META[getEventStatus(event)].label}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#fff',
                        backgroundColor: EVENT_STATUS_META[getEventStatus(event)].solid,
                      }}
                    />
                    <Chip
                      label={EVENT_TYPE_META[event.type].label}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#fff',
                        backgroundColor: EVENT_TYPE_META[event.type].solid,
                      }}
                    />
                  </Box>

                  {isRecordRoom(event) && (
                    <Chip
                      className="record-room-tag"
                      label={RECORD_ROOM_META.label}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#fff',
                        backgroundColor: RECORD_ROOM_META.solid,
                        transition: 'opacity 0.15s',
                      }}
                    />
                  )}

                  {canManageEvents && (
                    <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.5 }}>
                      <IconButton
                        className="action-btn"
                        size="small"
                        aria-label={`${event.title} 복제`}
                        disabled={duplicatingId === event.id}
                        onClick={(e) => handleDuplicate(e, event)}
                        sx={{
                          color: '#fff',
                          backgroundColor: 'rgba(0,0,0,0.4)',
                          backdropFilter: 'blur(2px)',
                          opacity: 0,
                          transition: 'opacity 0.15s',
                          '&:hover': { backgroundColor: 'rgba(29,161,242,0.95)' },
                        }}
                      >
                        <CopyIcon />
                      </IconButton>
                      <IconButton
                        className="action-btn"
                        size="small"
                        aria-label={`${event.title} 삭제`}
                        onClick={(e) => handleDelete(e, event)}
                        sx={{
                          color: '#fff',
                          backgroundColor: 'rgba(0,0,0,0.4)',
                          backdropFilter: 'blur(2px)',
                          opacity: 0,
                          transition: 'opacity 0.15s',
                          '&:hover': { backgroundColor: 'rgba(220,38,38,0.95)' },
                        }}
                      >
                        <TrashIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* 정보 */}
                <Box sx={{ p: 2 }}>
                  <Box sx={{ color: '#8a94a6', fontSize: 11, fontWeight: 700, mb: 0.5 }}>
                    {event.eventCode}
                  </Box>
                  <Box
                    sx={{
                      color: '#111827',
                      fontSize: 15,
                      fontWeight: 800,
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {event.title}
                  </Box>
                  <Box sx={{ color: '#475569', fontSize: 12.5, lineHeight: 1.8 }}>
                    <div>주최: {event.hostName}</div>
                    <div>일시: {formatDateKo(event.eventDate)}</div>
                  </Box>
                </Box>
                  </Paper>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </div>

      {canManageEvents && (
        <EventCreateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}

      <Dialog fullWidth maxWidth="xs" onClose={closeDeleteDialog} open={deleteTarget !== null}>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 800 }}>대회 삭제</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ color: '#374151', fontSize: 14, lineHeight: 1.7 }}>
            '{deleteTarget?.title}' 대회를 삭제할까요?
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={deleting} onClick={closeDeleteDialog} variant="outlined">
            취소
          </Button>
          <Button color="error" disabled={deleting} onClick={confirmDelete} variant="contained">
            {deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
      {errorToast}
    </section>
  );
}

export default EventListPage;
